import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import { SmsCreditSubscriptionService } from './sms-credit-subscription.service';
import {
  SmsSubscriptionCredits,
  SmsCreditsUpdatePayload,
} from '../models/SmsSubscriptionCredits';

const initialState: SmsSubscriptionCredits = {
  current: {
    totalCredits: 0,
    totalSubscriptionCredits: 0,
    subscriptionCredits: 0,
    topUpCredits: 0,
  },
  upcomingSubscription: {
    id: null,
    credits: 0,
    effectiveDate: null,
    blocked: false,
  },
  subscriptionChange: {
    id: null,
    effectiveDate: null,
    credits: 0,
    canDelete: false,
  },
  topUps: [],
};

@Injectable()
export class SmsCreditSubscriptionStateService {
  private readonly smsSubscriptionCreditsSubject$ = new BehaviorSubject<
    SmsSubscriptionCredits
  >(initialState);
  private readonly smsSubscriptionCredits$ = this.smsSubscriptionCreditsSubject$.asObservable();
  public readonly current$ = this.smsSubscriptionCredits$.pipe(
    map((state) => state.current),
    tap((current) => {
      this.currentSubscriptionCreditsSubject$.next(current.subscriptionCredits);
      this.currentTotalCreditsSubject$.next(current.totalCredits);
      this.currentTotalSubscriptionCreditsSubject$.next(
        current.totalSubscriptionCredits,
      );
      this.currentTopUpCreditsSubject$.next(current.topUpCredits);
    }),
  );
  public readonly currentTotalCreditsSubject$ = new BehaviorSubject<number>(0);
  public readonly currentTotalSubscriptionCreditsSubject$ = new BehaviorSubject<
    number
  >(0);
  public readonly currentTopUpCreditsSubject$ = new BehaviorSubject<number>(0);
  public readonly currentSubscriptionCreditsSubject$ = new BehaviorSubject<
    number
  >(0);

  public readonly upcomingSubscription$ = this.smsSubscriptionCredits$.pipe(
    map((state) => state.upcomingSubscription),
    tap((upcomingSubscription) => {
      this.upcomingSubscriptionEffectiveDateSubject$.next(
        upcomingSubscription?.effectiveDate,
      );
      this.upcomingSubscriptionCreditsSubject$.next(
        upcomingSubscription?.credits,
      );
    }),
  );
  public readonly upcomingSubscriptionCreditsSubject$ = new BehaviorSubject<
    number
  >(0);
  public readonly upcomingSubscriptionEffectiveDateSubject$ = new BehaviorSubject<
    string
  >(null);

  public readonly subscriptionChange$ = this.smsSubscriptionCredits$.pipe(
    map((state) => state.subscriptionChange),
    tap((subscriptionChange) =>
      this.subscriptionChangeEffectiveDateSubject$.next(
        subscriptionChange?.effectiveDate,
      ),
    ),
  );
  public readonly subscriptionChangeEffectiveDateSubject$ = new BehaviorSubject<
    string
  >(null);

  public readonly topUps$ = this.smsSubscriptionCredits$.pipe(
    map((state) => state.topUps),
  );

  private readonly renewalDateLoadingSubject$ = new BehaviorSubject<boolean>(
    false,
  );
  public readonly renewalDateLoading$ = this.renewalDateLoadingSubject$.asObservable();

  private readonly packageLoadingSubject$ = new BehaviorSubject<boolean>(false);
  public readonly packageLoading$ = this.packageLoadingSubject$.asObservable();

  private readonly topUpLoadingSubject$ = new BehaviorSubject<boolean>(false);
  public readonly topUpLoading$ = this.topUpLoadingSubject$.asObservable();

  private readonly fetchSmsSubscriptionCreditsError =
    'Failed to fetch sms credit subscriptions';
  private readonly removeUpcomingSubscriptionError =
    'Failed to remove upcoming subscription';
  private readonly removeUpcomingTopUpError =
    'Failed to remove upcoming top up';

  private readonly updateRenewalDateError = 'Failed to update renewal date';
  private readonly updatePackageError = 'Failed to update package';
  private readonly updateTopUpError = 'Failed to update top up';
  private readonly resetCreditsError = 'Failed to reset credits';

  constructor(
    private readonly smsCreditSubscriptionService: SmsCreditSubscriptionService,
    private readonly toastr: ToastrService,
  ) {}

  public getSmsSubscriptionCredits(accountId: string) {
    this.smsCreditSubscriptionService
      .getSmsSubscriptionCredits(accountId)
      .subscribe(
        (credits) => this.smsSubscriptionCreditsSubject$.next(credits),
        (error) =>
          this.toastr.error(
            error?.error?.error ?? this.fetchSmsSubscriptionCreditsError,
          ),
      );
  }

  public removeUpcomingSubscription(accountId: string, id: number) {
    const delete$ = this.smsCreditSubscriptionService
      .removeUpcomingSubscription(accountId, id)
      .pipe(
        catchError((error) =>
          throwError(
            error?.error?.error ?? this.removeUpcomingSubscriptionError,
          ),
        ),
      );

    this.executeAndFetch(accountId, delete$);
  }

  public removeUpcomingTopUp(accountId: string, id: number) {
    const delete$ = this.smsCreditSubscriptionService
      .removeUpcomingTopUp(accountId, id)
      .pipe(
        catchError((error) =>
          throwError(error?.error?.error ?? this.removeUpcomingTopUpError),
        ),
      );

    this.executeAndFetch(accountId, delete$);
  }

  public renewalDateUpdate(
    accountId: string,
    payload: SmsCreditsUpdatePayload,
  ) {
    this.renewalDateLoadingSubject$.next(true);
    const updateRenewalDate$ = this.smsCreditSubscriptionService
      .updateRenewalDate(accountId, payload)
      .pipe(
        finalize(() => this.renewalDateLoadingSubject$.next(false)),
        catchError((error) =>
          throwError(error?.error?.error ?? this.updateRenewalDateError),
        ),
      );

    this.executeAndFetch(accountId, updateRenewalDate$);
  }

  public packageUpdate(accountId: string, payload: SmsCreditsUpdatePayload) {
    this.packageLoadingSubject$.next(true);
    const updatePackage$ = this.smsCreditSubscriptionService
      .updateSmsSubscriptionPackage(accountId, payload)
      .pipe(
        finalize(() => this.packageLoadingSubject$.next(false)),
        catchError((error) =>
          throwError(error?.error?.error ?? this.updatePackageError),
        ),
      );

    this.executeAndFetch(accountId, updatePackage$);
  }

  public resetCredits(accountId: string) {
    this.packageLoadingSubject$.next(true);
    const resetCredits$ = this.smsCreditSubscriptionService
      .resetCredits(accountId)
      .pipe(
        finalize(() => this.packageLoadingSubject$.next(false)),
        catchError((error) =>
          throwError(error?.error?.error ?? this.resetCreditsError),
        ),
      );

    this.executeAndFetch(accountId, resetCredits$);
  }

  public topUpUpdate(accountId: string, payload: SmsCreditsUpdatePayload) {
    this.topUpLoadingSubject$.next(true);
    const updateTopUp$ = this.smsCreditSubscriptionService
      .updateSmsSubscriptionTopUp(accountId, payload)
      .pipe(
        finalize(() => this.topUpLoadingSubject$.next(false)),
        catchError((error) =>
          throwError(error?.error?.error ?? this.updateTopUpError),
        ),
      );

    this.executeAndFetch(accountId, updateTopUp$);
  }

  private executeAndFetch(accountId: string, action$: Observable<unknown>) {
    const get$ = this.smsCreditSubscriptionService
      .getSmsSubscriptionCredits(accountId)
      .pipe(
        catchError((error) =>
          throwError(
            error?.error?.error ?? this.fetchSmsSubscriptionCreditsError,
          ),
        ),
      );
    this.executeAction(action$, get$).subscribe(
      (credits) => this.smsSubscriptionCreditsSubject$.next(credits),
      (error) => this.toastr.error(error),
    );
  }

  private executeAction(
    obs1$: Observable<unknown>,
    obs2$: Observable<SmsSubscriptionCredits>,
  ) {
    return obs1$.pipe(
      switchMap(() => obs2$),
      take(1),
    );
  }
}
